import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PusherService, NewTokenData } from 'app/common/_services/pusher.service';
import { Subscription } from 'rxjs';
import { MainPageEndpointsService, SoundPlaybackService } from 'app/common/index';
import { getAutoRedirectMode, getAutoRedirectPath, setAutoRedirectPath } from 'app/common/autoRedirect.util';
import { CounterCustomizationAttributes } from 'app/common/_types/customizations.types';
import { DEFAULT_COUNTER_CUSTOMIZATION } from 'app/common/_constants/customization.constants';

interface RecentServingToken {
  tokenNumber: string;
  counterName: string;
}

@Component({
  selector: 'app-recent-serving-counters',
  templateUrl: './recent-serving-counters.component.html',
  styleUrls: ['./recent-serving-counters.component.scss'],
})
export class RecentServingCountersComponent implements OnInit, OnDestroy {
  timer: Subscription | null = null;
  recentServingTokens: RecentServingToken[] = [];
  customization: CounterCustomizationAttributes = DEFAULT_COUNTER_CUSTOMIZATION;
  loading = true;
  count = 2;

  constructor(
    private route: ActivatedRoute,
    private pusherService: PusherService,
    private mainPageEndpoints: MainPageEndpointsService,
    private router: Router,
    private soundPlaybackService: SoundPlaybackService,
  ) {}

  ngOnInit(): void {
    this.initPusherSubscription();
    this.loadLatestServingTokens();
    this.loadCounterCustomization();
    this.count = this.route.snapshot.params['count'] || 2;
    this.handleOldAutoRedirectUrl();
  }

  ngOnDestroy(): void {
    if (this.timer) this.timer.unsubscribe();
    this.timer = null;
  }

  // Handles old auto-redirect URLs with count as a query parameter
  handleOldAutoRedirectUrl() {
    const autoRedirectMode = getAutoRedirectMode();
    const path = getAutoRedirectPath();
    const isNewPath = !!path?.match(/recent_serving_counters\/\d+/)?.[0];
    if (autoRedirectMode && !isNewPath) setAutoRedirectPath(this.router.url);
  }

  getQueryParams() {
    return this.route.snapshot.queryParams;
  }

  initPusherSubscription() {
    const branchId = this.route.snapshot.params['branchId'];
    this.pusherService.connectTokenBroadcast(branchId, this.handleNewTokenData);
  }

  handleNewTokenData = (data: NewTokenData) => {
    const { counterName, tokenNumber, status } = data.data.attributes;

    this.soundPlaybackService.handleSoundPlayForTokenEvent(data);

    if (status === 'serving') {
      const newRecentServingToken = {
        tokenNumber: data.data.attributes.tokenNumber,
        counterName: data.data.attributes.counterName,
      };

      const existingCounterIndex = this.recentServingTokens.findIndex((it) => it.counterName === counterName);
      const isSameTokenNumber = this.recentServingTokens[existingCounterIndex]?.tokenNumber === tokenNumber;

      if (existingCounterIndex > -1 && isSameTokenNumber) {
        this.recentServingTokens.splice(existingCounterIndex, 1, newRecentServingToken);
        return;
      }

      if (this.recentServingTokens.length >= this.count) this.recentServingTokens.pop();
      this.recentServingTokens.unshift(newRecentServingToken);
    }
  };

  loadLatestServingTokens() {
    this.mainPageEndpoints.getQueuesData(true).subscribe((data) => {
      const counters = data.included || [];

      this.recentServingTokens = counters
        .filter((counter) => ['serving', 'served'].includes(counter.attributes.currentTokenNumber?.status))
        .sort(
          (a, b) =>
            new Date(b.attributes.currentTokenNumber?.startTime).getTime() -
            new Date(a.attributes.currentTokenNumber?.startTime).getTime(),
        )
        .slice(0, this.count)
        .map((counter) => ({
          tokenNumber: counter.attributes.currentTokenNumber.tokenNumber,
          counterName: counter.attributes.name,
        }));
    });
  }

  loadCounterCustomization() {
    this.loading = true;

    this.mainPageEndpoints.getCounterCustomization().subscribe((res) => {
      const customization = res.data.attributes;

      if (!res?.data?.attributes) return;

      document.documentElement.style.setProperty('--counter-customization-text-color', customization.fontColor);
      document.documentElement.style.setProperty(
        '--counter-customization-background-color',
        customization.backgroundColor,
      );

      setTimeout(() => (this.loading = false), 500);
    });
  }

  refresh() {
    window.location.reload();
  }
}
